import { useEffect, useState } from "react";
import api, { formatApiError, buildDownloadUrl, downloadFile } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePaywall } from "@/context/PaywallContext";
import { LogoNameEditor } from "@/components/LogoNameEditor";
import { Link } from "react-router-dom";
import { Thumb } from "@/components/RecentLogos";
import { Download, Trash2, Package, ImageIcon, Lock } from "lucide-react";

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (job_id) => {
    if (!window.confirm("Delete this vector job? Files will be removed.")) return;
    try {
      await api.delete(`/jobs/${job_id}`);
      setItems((prev) => prev.filter((i) => i.job_id !== job_id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const paywall = usePaywall();
  const handleDownload = async (job, path, filename) => {
    try {
      await downloadFile(path, filename);
    } catch (err) {
      if (err.status === 402) paywall.open(job);
      else toast.error(err.message || "Download failed");
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="history-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2">Archive</div>
          <h1 className="text-3xl md:text-4xl tracking-tighter font-black">Vector history</h1>
          <p className="text-[#52525B] mt-3 text-sm max-w-xl">
            Every logo you have vectorized lives here. Re-download your files
            anytime, or clear out the noise.
          </p>
        </div>

        {loading ? (
          <div className="text-xs uppercase tracking-[0.3em] text-[#52525B] animate-pulse">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-black/10 bg-[#F8F9FA] p-12 text-center" data-testid="history-empty">
            <ImageIcon className="w-8 h-8 mx-auto text-[#ED5A24] mb-4" strokeWidth={1.5} />
            <div className="text-lg font-medium mb-2">No vectorizations yet</div>
            <p className="text-sm text-[#52525B] mb-6">Head to the dashboard to convert your first logo.</p>
          </div>
        ) : (
          <div className="border border-black/10" data-testid="history-list">
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#0A0A0B] text-white text-[11px] uppercase tracking-[0.2em] font-bold">
              <div className="col-span-4">File</div>
              <div className="col-span-2">Colors</div>
              <div className="col-span-3">Created</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {items.map((job) => (
              <div
                key={job.job_id}
                data-testid={`history-row-${job.job_id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-5 border-t border-black/10 first:border-t-0 items-center bg-white hover:bg-[#F8F9FA] transition-colors duration-200"
              >
                <div className="md:col-span-4 flex items-center gap-3">
                  <Link to={`/dashboard?job=${job.job_id}`} data-testid={`history-thumb-${job.job_id}`}
                    className="relative shrink-0 w-14 h-14 border border-black/10 bg-[#F8F9FA] flex items-center justify-center p-1 hover:border-[#ED5A24] transition-colors duration-200">
                    <Thumb src={buildDownloadUrl(job.thumb)} alt={job.name} />
                    {!job.unlocked && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#0A0A0B] text-white flex items-center justify-center" data-testid={`history-locked-${job.job_id}`}>
                        <Lock className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </Link>
                  <div className="min-w-0">
                    <LogoNameEditor
                      jobId={job.job_id}
                      name={job.name || job.filename}
                      className="text-sm font-medium"
                      onSaved={(name) => setItems((list) => list.map((j) => (j.job_id === job.job_id ? { ...j, name } : j)))}
                    />
                    <div className="text-[11px] text-[#52525B] truncate">{job.filename}</div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <span className="text-[11px] tracking-[0.15em] uppercase font-bold px-2 py-1 bg-[#0A0A0B] text-white">
                    {job.color_count === "full" ? "Full" : `${job.color_count}-color`}
                  </span>
                </div>
                <div className="md:col-span-3 text-xs text-[#52525B]">{formatDate(job.created_at)}</div>
                <div className="md:col-span-3 flex items-center md:justify-end gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => handleDownload(job, job.downloads.zip, `${job.filename.replace(/\.[^.]+$/, "")}_vector_bundle.zip`)}
                    data-testid={`history-download-${job.job_id}`}
                    className="inline-flex items-center gap-2 rounded-none bg-[#ED5A24] hover:bg-[#C44619] text-white text-[11px] uppercase tracking-[0.2em] font-bold h-9 px-4 transition-colors duration-200 cursor-pointer"
                  >
                    <Package className="w-3.5 h-3.5" /> Bundle
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownload(job, job.downloads.svg, `${job.filename.replace(/\.[^.]+$/, "")}.svg`)}
                    data-testid={`history-svg-${job.job_id}`}
                    className="inline-flex items-center gap-2 rounded-none border border-black/15 hover:bg-[#0A0A0B] hover:text-white text-[#0A0A0B] text-[11px] uppercase tracking-[0.2em] font-bold h-9 px-3 transition-colors duration-200 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" /> SVG
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`history-delete-${job.job_id}`}
                    onClick={() => remove(job.job_id)}
                    className="rounded-none h-9 w-9 p-0 text-[#52525B] hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
